import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiLock, FiUnlock, FiFileText } from 'react-icons/fi';
import UserModal from '../components/UserModal';
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invites, setInvites] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchInvites();
    axios.get('/api/companies/').then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
      setCompanies(list);
    }).catch(() => setCompanies([]));
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users/');
      setUsers(response.data.results || response.data);
    } catch (error) {
      toast.error('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const response = await axios.get('/api/auth/manager-invites/');
      setInvites(response.data || []);
    } catch (error) {
      toast.error('Ошибка загрузки приглашений');
    }
  };

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Ссылка скопирована');
    } catch (error) {
      toast.error('Не удалось скопировать ссылку');
    }
  };

  const createInvite = async (multiUse = false) => {
    setInviteLoading(true);
    try {
      const response = await axios.post('/api/auth/manager-invites/', {
        email: inviteEmail,
        multi_use: multiUse,
      });
      setInviteEmail('');
      setInvites((prev) => [response.data, ...prev]);
      toast.success(multiUse ? 'Создана общая ссылка на 3 дня (несколько регистраций)' : 'Приглашение создано');
      if (response.data?.register_url) {
        copyText(response.data.register_url);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка создания приглашения');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      return;
    }

    try {
      await axios.delete(`/api/users/${id}/`);
      toast.success('Пользователь удален');
      fetchUsers();
    } catch (error) {
      toast.error('Ошибка удаления пользователя');
    }
  };

  const handleToggleBlock = async (user) => {
    try {
      await axios.patch(`/api/users/${user.id}/`, {
        is_blocked: !user.is_blocked,
      });
      toast.success(user.is_blocked ? 'Пользователь разблокирован' : 'Пользователь заблокирован');
      fetchUsers();
    } catch (error) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    fetchUsers();
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  const filteredUsers = companyFilter
    ? users.filter((u) => String(u.company || '') === companyFilter)
    : users;

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Пользователи</h1>
        <div className="users-header-actions">
          {companies.length > 1 && (
            <select
              className="users-company-filter"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <option value="">Все фирмы</option>
              {companies.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
            <FiPlus /> Добавить пользователя
          </button>
        </div>
      </div>

      <div className="invite-panel">
        <h3>Приглашение менеджера по ссылке</h3>
        <p className="invite-hint">
          Одна персональная ссылка — одна регистрация. Общая ссылка на 3 дня — любое число менеджеров по одной ссылке до истечения срока.
        </p>
        <div className="invite-row">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="Email (опционально, только для персональной ссылки)"
          />
          <button className="btn btn-secondary" onClick={() => createInvite(false)} disabled={inviteLoading}>
            {inviteLoading ? 'Создание...' : 'Персональная ссылка'}
          </button>
          <button className="btn btn-primary" onClick={() => createInvite(true)} disabled={inviteLoading} title="Одна ссылка для всех, 3 дня">
            {inviteLoading ? 'Создание...' : 'Общая ссылка на 3 дня'}
          </button>
        </div>
        {invites.length > 0 && (
          <div className="invite-list">
            {invites.slice(0, 8).map((invite) => (
              <div key={invite.id} className="invite-item">
                <div>
                  <div className="invite-email">
                    {invite.is_multi_use ? 'Общая ссылка (много регистраций)' : invite.email || 'Персональная, без email'}
                  </div>
                  <div className="invite-meta">
                    {invite.is_multi_use
                      ? `Действует до ${new Date(invite.expires_at).toLocaleString('ru-RU')}`
                      : `${invite.is_used ? 'Использовано' : 'Активно'} до ${new Date(invite.expires_at).toLocaleString('ru-RU')}`}
                  </div>
                </div>
                <button className="icon-btn" onClick={() => copyText(invite.register_url)} title="Скопировать ссылку">
                  Копировать
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Имя</th>
              <th>Фирма</th>
              <th>Роль</th>
              <th>Статус</th>
              <th>Дата регистрации</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>{user.email}</td>
                <td>{user.first_name} {user.last_name}</td>
                <td>{user.company_name || '—'}</td>
                <td>
                  <span className={`role-badge ${user.role}`}>
                    {user.role === 'moderator' ? 'Модератор' : 'Менеджер'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${user.is_blocked ? 'blocked' : 'active'}`}>
                    {user.is_blocked ? 'Заблокирован' : 'Активен'}
                  </span>
                </td>
                <td>{new Date(user.date_joined).toLocaleDateString('ru-RU')}</td>
                <td>
                  <div className="table-actions">
                    <Link
                      to={`/users/${user.id}/logs`}
                      className="icon-btn"
                      title="Просмотр логов"
                    >
                      <FiFileText />
                    </Link>
                    <button className="icon-btn" onClick={() => handleEdit(user)}>
                      <FiEdit />
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => handleToggleBlock(user)}
                      title={user.is_blocked ? 'Разблокировать' : 'Заблокировать'}
                    >
                      {user.is_blocked ? <FiUnlock /> : <FiLock />}
                    </button>
                    <button className="icon-btn danger" onClick={() => handleDelete(user.id)}>
                      <FiTrash2 />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UserModal
        open={modalOpen}
        onClose={handleCloseModal}
        user={editingUser}
      />
    </div>
  );
};

export default Users;
